import Ember from 'ember';
import config from 'invite-sign/config/environment';
import {
  decideKioskRecovery,
  KIOSK_ENTRY_URL_KEY,
  KIOSK_LAST_RECOVERY_AT_KEY,
  KIOSK_RECOVERY_COUNT_KEY,
} from 'invite-sign/utils/kiosk-recovery';

// SQ-7544: on a shared-device kiosk, an uncaught error can leave a wedged white
// screen that only a device reboot clears. This installs global error handlers
// that auto-recover the kiosk to its welcome screen, with a loop guard so a
// persistent error can't turn into an endless reload-thrash. Non-shared flows
// (QR / personal invites) are untouched and fall through to the normal error
// route. The recovery decision lives in `utils/kiosk-recovery` and is unit
// tested; this file is the thin wiring around it.

function readNumber(key: string): number | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

// A single wedge routinely produces several uncaught errors before the
// recovery navigation unloads the page (a broken renderer rethrows on every
// re-render attempt). Count the incident once so it can't burn the whole
// loop-guard budget and leave a later, genuine wedge unrecovered.
let recoveryInFlight = false;

function handleKioskError(error: unknown): void {
  if (recoveryInFlight) return;

  let entryUrl: string | null = null;
  try {
    entryUrl = sessionStorage.getItem(KIOSK_ENTRY_URL_KEY);
  } catch {
    entryUrl = null;
  }

  // Not a shared device: leave the error to the normal error route.
  if (!entryUrl) return;

  // Surface the error so device-side console capture can see it (our key
  // diagnostic signal for the underlying wedge). Log the stack as a string:
  // the Neat Frame's console capture records only the message text, and the
  // stack's chunk URL + line/col let us source-map the throw site.
  const detail = error instanceof Error && error.stack ? error.stack : error;
  // eslint-disable-next-line no-console
  console.error('[kiosk-recovery] uncaught error on shared device', detail);

  const decision = decideKioskRecovery({
    entryUrl,
    now: Date.now(),
    lastRecoveryAt: readNumber(KIOSK_LAST_RECOVERY_AT_KEY),
    recentCount: readNumber(KIOSK_RECOVERY_COUNT_KEY) ?? 0,
  });

  if (!decision.recover) return;

  recoveryInFlight = true;

  try {
    sessionStorage.setItem(KIOSK_LAST_RECOVERY_AT_KEY, String(Date.now()));
    sessionStorage.setItem(KIOSK_RECOVERY_COUNT_KEY, String(decision.nextCount));
  } catch {
    // best-effort; recover anyway
  }

  window.location.replace(entryUrl);
}

export function initialize(): void {
  // Never install in tests: it would override Ember.onerror (which the test
  // framework relies on) and could trigger a navigation mid-test.
  if (config.environment === 'test') return;

  const priorOnerror = Ember.onerror;
  Ember.onerror = (error: Error): void => {
    handleKioskError(error);
    if (typeof priorOnerror === 'function') priorOnerror(error);
  };

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent): void => {
    handleKioskError(event.reason);
  });

  // Render-phase and native-callback errors (e.g. a modifier or a camera
  // callback throwing outside the runloop) bypass Ember.onerror entirely and
  // can leave Glimmer unable to re-render — the one wedge the hooks above
  // cannot recover from (seen live on the Neat Frame, SQ-7544). A bubble-phase
  // 'error' listener receives only uncaught runtime errors (resource-load
  // failures do not bubble to window), and unlike assigning window.onerror it
  // overwrites nothing — default console logging and other handlers (e.g.
  // LogRocket's) are untouched.
  window.addEventListener('error', (event: ErrorEvent): void => {
    handleKioskError(event.error ?? event.message);
  });
}

export default { initialize };
